import { fillTemplate, formatYMD, getOverdueInvoices, sumAmounts, ymdToLocalDate } from './resignation/core/utils'
import SoftLimitCalendar, { startOfMonth } from 'components/pickers/SoftLimitCalendar'
import { getBalance, selectUserId } from 'containers/Login/loginSlice'
import { getWarehouses } from 'containers/Warehouses/warehousesSlice'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useHistory, useParams } from 'react-router-dom'
import PayNow from './resignation/Termination/PayNow'
import { SECTION_TYPES } from 'containers/Warehouse'
import ArrowBtn from 'components/buttons/ArrowBtn'
import Checkbox from 'components/inputs/Checkbox'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { dateToYMDformat } from 'utils/date'
import { displayPrice } from 'utils/string'
import { GET, POST } from 'rest/request'
import Option from 'components/Option'
import * as urls from 'rest/urls'
import React from 'react'

type Step = 'choose' | 'calendar' | 'payNow'
type SettlementChoice = 'schedule' | 'payNow'

interface PayNowState {
  payNow?: payments.TerminationPayNow | null
  overdueInvoices: payments.QuoteOverdueInvoice[]
  /** data przedluzenia do ekranu platnosci; '' = bezterminowo */
  date: string
}

const addDaysYMD = (ymd: string, days: number) => {
  const date = ymdToLocalDate(ymd)
  date.setDate(date.getDate() + days)
  return dateToYMDformat(date)
}

/** 'YYYY-MM-DD HH:MM:SS' -> { date: 'DD.MM.YYYY', time: 'HH:MM' } */
const splitDateTime = (value: string) => ({ date: formatYMD(value), time: value.slice(11, 16) })

/** obecny koniec najmu: zgloszone zakonczenie wygrywa z koncem umowy */
const getCurrentEnd = (warehouse: warehouse.Warehouse) => {
  const { resignation, fullDateTo } = warehouse
  const end = resignation?.type === 'termination' && resignation.date ? resignation.date : fullDateTo
  return end ? end.slice(0, 10) : undefined
}

/** request() odrzuca promise z JSON.stringify(body) bledu */
const parseError = (error: unknown): string[] => {
  try {
    const data = JSON.parse(String(error))
    if (Array.isArray(data?.errors)) return data.errors.map(String)
    if (data?.errors && typeof data.errors === 'object') return Object.values(data.errors).flat().map(String)
    if (typeof data?.message === 'string') return [data.message]
    if (typeof data?.error === 'string') return [data.error]
  } catch { }
  return []
}

/** wycena przedluzenia = GET termination/quote z data po koncu najmu; odpowiedzi na starsze daty sa ignorowane */
const useExtensionQuote = (contractId: string, date?: string) => {
  const [quote, setQuote] = useState<payments.Quote>()
  const [quoteDate, setQuoteDate] = useState<string>()
  const [isLoading, setLoading] = useState(false)
  const lastRequestId = useRef(0)

  const load = useCallback(async (ymd: string) => {
    const requestId = ++lastRequestId.current
    setLoading(true)
    try {
      const response: payments.Quote = await GET(urls.contractTermination(contractId, ymd))
      if (requestId !== lastRequestId.current) return
      setQuote(response)
      setQuoteDate(ymd)
    } catch (error) {
      console.error(error)
    } finally {
      if (requestId === lastRequestId.current) setLoading(false)
    }
  }, [contractId])

  useEffect(() => {
    if (!date) return
    const timeout = setTimeout(() => load(date), 200)
    return () => clearTimeout(timeout)
  }, [date, load])

  return { quote, quoteDate, isLoading, reload: () => date && load(date) }
}

/**
 * Przedluzenie umowy dlugoterminowej (dok. frontu 2026-10-08) - ten sam proces co zakonczenie:
 * wycena (termination/quote z data po koncu najmu) -> wybor "Oplac teraz" -> POST extend
 * -> polling dokumentu (contracts/{id}/documents/{id}) -> platnosc. Karta NIE jest obciazana automatycznie.
 */
const LongTermExtension: React.FC<{ warehouse: warehouse.Warehouse }> = ({ warehouse }) => {
  const { contractId, canExtend, canExtendUntil, extendMaxDate, resignation, terminationTerms } = warehouse
  const userId = useReduxSelector(selectUserId)
  const { id } = useParams<warehouse.PageParams>()

  const [step, setStep] = useState<Step>('choose')
  const [indefinite, setIndefinite] = useState(false)
  const [date, setDate] = useState<string>()
  const [settlement, setSettlement] = useState<SettlementChoice>('schedule')
  const [isSending, setSending] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [payNowState, setPayNowState] = useState<PayNowState>()
  const { quote: response, quoteDate, isLoading, reload } = useExtensionQuote(contractId, step === 'calendar' ? date : undefined)

  const { goBack, push } = useHistory()
  const dispatch = useReduxDispatch()
  const { t, lang } = useTranslation('warehouse')
  const te = useTranslation('warehouse.extensionCalendar').t
  const tc = useTranslation('warehouse.terminationCalendar').t

  const currentEnd = response?.extension?.currentEndDate?.slice(0, 10) ?? getCurrentEnd(warehouse)
  const minDate = currentEnd ? addDaysYMD(currentEnd, 1) : dateToYMDformat()
  const maxDate = response?.extension?.maxEndDate ?? extendMaxDate ?? undefined
  const until = canExtendUntil ? splitDateTime(canExtendUntil) : undefined
  const terminationDate = resignation?.type === 'termination' ? resignation.date : undefined

  /** data zakonczenia potwierdzona oplaceniem faktury - przedluzenie niemozliwe */
  const isDateAccepted = !!resignation?.dateAccepted
  const isUnavailable = isDateAccepted || canExtend === false

  const isQuoteCurrent = !!response && !!date && quoteDate === date
  const quote = isQuoteCurrent ? response : undefined
  const extension = quote?.extension

  const overdueInvoices = getOverdueInvoices(response)
  const overdueAmount = sumAmounts(overdueInvoices.map(invoice => invoice.remainingAmount))
  const paidUntil = response?.paidUntil

  const dueNowAmount = Number(quote?.amounts?.dueNowGross || 0)
  const dueLaterAmount = Number(quote?.amounts?.dueLaterGross || 0)
  const payNowAmount = Number(quote?.amounts?.payNowGross || 0)
  // jak przy zakonczeniu: wybor ma sens, gdy cos zostaloby zafakturowane pozniej (przez workera)
  const showSettlementChoice = !!quote?.flags?.canPayNow && payNowAmount > 0 && dueLaterAmount > 0

  const quoteErrors = Array.from(new Set([...(extension?.errors ?? []), ...(quote?.errors ?? []).map(String)]))
  const isBlocked = isUnavailable || !extension || !extension.canExtend

  useEffect(() => {
    if (!showSettlementChoice) setSettlement('schedule')
  }, [showSettlementChoice])

  const finish = () => {
    dispatch(getWarehouses(userId))
    goBack()
  }

  const send = async (body: payments.ExtendBody) => {
    if (isSending) return
    setSending(true)
    setErrors([])
    try {
      const result: payments.ExtendResponse = await POST(urls.contractExtend(contractId), { body }, false)
      if (result?.result !== 'ok') {
        setErrors(result?.errors?.length ? result.errors.map(String) : [te('extensionFailed')])
        setSending(false)
        return
      }
      // dokument z przedluzenia (wybrane "Oplac teraz" albo faktura "teraz" przy auto) -> polling i platnosc
      if ((result.payNow?.documents?.length ?? 0) > 0 || (('settlement' in body) && body.settlement === 'payNow')) {
        dispatch(getWarehouses(userId))
        setPayNowState({ payNow: result.payNow, overdueInvoices, date: 'newDate' in body ? body.newDate : '' })
        setStep('payNow')
        return
      }
      finish()
    } catch (error) {
      const parsed = parseError(error)
      setErrors(parsed.length ? parsed : [te('extensionFailed')])
      setSending(false)
      // np. zajetosc pojawila sie w miedzyczasie - odswiezamy wycene
      if (step === 'calendar') reload()
    }
  }

  if (step === 'payNow') {
    return <div className='menu__section menu__extension'>
      <PayNow
        variant='extension'
        contractId={contractId}
        payNow={payNowState?.payNow}
        overdueInvoices={payNowState?.overdueInvoices ?? []}
        selectedData={{ date: payNowState?.date ?? '' }}
        close={remotePaymentId => {
          if (remotePaymentId) dispatch(getBalance())
          finish()
        }}
      />
    </div>
  }

  const unavailableInfo = isUnavailable && <div className='resignation__info --error'>
    {te(isDateAccepted ? 'dateAccepted' : 'unavailable')}
  </div>

  const terminationInfo = terminationDate && !isUnavailable && <div className='resignation__tile --warning'>
    {fillTemplate(te('cancelsTermination'), { date: formatYMD(terminationDate) })}
  </div>

  if (step === 'calendar') {
    return (
      <div className='menu__section menu__extension'>
        <div className='title'>
          <div>{t('extendingYourRental')}</div>
          <div>{t('selectEndDate')}</div>
        </div>

        <div className='resignation__main'>
          <div className='resignation__content'>
            <div className='resignation__tile --warning'>
              {currentEnd && <>{te('currentEnd')} <b>{formatYMD(currentEnd)}</b>. </>}
              {maxDate
                ? <>{te('maxEndPrefix')} <b>{formatYMD(maxDate)}</b>.</>
                : te('noMaxEnd')}
            </div>

            {overdueInvoices.length > 0
              ? <div className='resignation__tile --error --row'>
                <i className='icon icon--warning2 s16' />
                <span>{tc('unpaidInvoices')} ({displayPrice(overdueAmount)})</span>
                <button
                  type='button'
                  className='resignation__tile__action'
                  onClick={() => push(`/warehouses/${id}/${SECTION_TYPES.invoices}`)}>
                  {t('pay2')}
                </button>
              </div>
              : paidUntil
                ? <div className='resignation__tile --success'>
                  {tc('paidUntil')} <b>{formatYMD(paidUntil)}</b>
                </div>
                : null}

            <div className='resignation__calendar popup__calendar__container'>
              <SoftLimitCalendar
                value={date ? ymdToLocalDate(date) : null}
                locale={lang}
                className='--light'
                minDate={startOfMonth(currentEnd ?? minDate)}
                softMinDate={minDate}
                softMaxDate={maxDate}
                getBlockedModal={(reason) => reason === 'beforeMin'
                  ? {
                    title: te('tooEarlyTitle'),
                    description: fillTemplate(te('tooEarlyDesc'), { date: formatYMD(minDate) }),
                  }
                  : {
                    title: te('occupiedTitle'),
                    description: fillTemplate(te('occupiedDesc'), { date: formatYMD(maxDate) }),
                  }}
                showNeighboringMonth={false}
                onChange={value => {
                  setDate(dateToYMDformat(value as Date))
                  setErrors([])
                }}
              />
            </div>

            {quote && <div className='resignation__notes'>
              {terminationInfo}

              {dueNowAmount > 0 && <div className='resignation__tile --error'>
                {te('dueNow')} <b>{displayPrice(dueNowAmount)}</b>
              </div>}

              {dueLaterAmount > 0 && !showSettlementChoice && <div className='resignation__tile'>
                {quote.invoicing?.nextInvoiceDate
                  ? fillTemplate(te('dueLaterDate'), { date: formatYMD(quote.invoicing.nextInvoiceDate) })
                  : te('dueLater')}
              </div>}

              {showSettlementChoice && !isBlocked && <div className='resignation__settlement'>
                <div className='resignation__settlement__title'>{te('settlementTitle')}</div>
                <Checkbox
                  name='extension-settlement-schedule'
                  checked={settlement === 'schedule'}
                  changeHandler={() => setSettlement('schedule')}
                  label={<>
                    {tc('settlementSchedule')}
                    {quote.invoicing?.nextInvoiceDate && <span className='resignation__settlement__hint'>
                      {fillTemplate(tc('settlementScheduleNextInvoice'), { date: formatYMD(quote.invoicing.nextInvoiceDate) })}
                    </span>}
                  </>}
                />
                <Checkbox
                  name='extension-settlement-pay-now'
                  checked={settlement === 'payNow'}
                  changeHandler={() => setSettlement('payNow')}
                  label={<>
                    {tc('settlementPayNow')} <b>{displayPrice(sumAmounts([payNowAmount, overdueAmount]))}</b>
                    {overdueAmount > 0 && ` ${fillTemplate(tc('includingOverdue'), { amount: displayPrice(overdueAmount) })}`}
                  </>}
                />
              </div>}
            </div>}

            {quoteErrors.map((message, idx) => <div key={idx} className='resignation__info --error'>{message}</div>)}
            {unavailableInfo}
            {errors.map((message, idx) => <div key={idx} className='resignation__info --error'>{message}</div>)}
          </div>

          <div className='resignation__buttons'>
            <ArrowBtn
              enlarged
              text={t('confirm')}
              disabled={!date || !isQuoteCurrent || isBlocked}
              isLoading={isSending || (isLoading && !!date && !isQuoteCurrent)}
              action={() => {
                if (!date || !isQuoteCurrent || isBlocked) return
                send({ newDate: date, settlement: showSettlementChoice && settlement === 'payNow' ? 'payNow' : 'auto' })
              }}
            />
            <BackBtn
              text={t('back')}
              action={() => {
                setErrors([])
                setStep('choose')
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  // bezterminowo nie da sie, gdy na obiekcie jest pozniejsza umowa / rezerwacja
  const canIndefinite = !extendMaxDate

  return (
    <div className='menu__section menu__extension'>
      <div className='title'>
        <div>{t('extendingYourRental')}</div>
        <div>{t('selectRentalPeriod')}</div>
      </div>

      <div className='extension__main'>
        <div className='extension__steps --center'>
          {canIndefinite && <>
            <Option
              secondaryTheme
              selected={indefinite}
              title={t('forAnIndefinitePeriod')}
              desc={t('permanentContract')}
              buttonText={t(indefinite ? 'selected' : 'select')}
              icon='infinite'
              action={() => {
                if (!isUnavailable) setIndefinite(true)
              }}
            />
            <div className='or'>{t('or')}</div>
          </>}

          <Option
            secondaryTheme
            title={t('forAFixedTerm')}
            desc={t('contractForAFixedPeriod')}
            buttonText={t('select')}
            icon='calendar-bold'
            action={() => {
              if (isUnavailable) return
              setErrors([])
              setStep('calendar')
            }}
          />

          {!canIndefinite && <div className='resignation__tile --warning'>
            {fillTemplate(te('indefiniteUnavailable'), { date: formatYMD(addDaysYMD(extendMaxDate!, 1)) })}
          </div>}

          {until && !isUnavailable && <div className='resignation__tile'>
            {fillTemplate(te('canExtendUntil'), until)}
          </div>}

          {terminationInfo}

          {terminationTerms?.infoForClient && <div className='calendar__info'>
            <b>{t('noticePeriod')}: </b>
            <div>{terminationTerms.infoForClient}</div>
          </div>}

          {unavailableInfo}
          {errors.map((message, idx) => <div key={idx} className='resignation__info --error'>{message}</div>)}
        </div>

        <div className='extension__buttons'>
          <ArrowBtn
            enlarged
            text={t('confirm')}
            isLoading={isSending}
            disabled={!indefinite || !canIndefinite || isUnavailable}
            action={() => {
              if (indefinite && canIndefinite && !isUnavailable) send({ indefiniteTime: true })
            }}
          />
          <BackBtn />
        </div>
      </div>
    </div>
  )
}

export default LongTermExtension
